import React from 'react';
import { Navigate, Route, useLocation } from 'react-router';
import { yardsAddress } from '../hooks/search/useLocationSearchParams';

// Old addresses Google still crawls (Search Console, 2026-09-25). With no route
// of their own they fell through to the catch-all, which served crawlers a login
// form (securspace-ui#3057). The query string rides along: HubSpot tracking
// parameters are on some of these links.
const LEGACY_REDIRECTS: Readonly<Record<string, string>> = {
  '/customer-signup': '/buyer-signup',
  '/partner-signup': '/supplier-signup',
  '/how-it-works': '/#how-it-works',
};

const LegacyRedirect = ({ to }: { to: string }) => {
  const { search, hash } = useLocation();
  const [pathname, targetHash] = to.split('#');
  // A target section (e.g. "How it works") wins; otherwise keep the incoming one.
  return <Navigate replace to={{ pathname, search, hash: targetHash ? `#${targetHash}` : hash }} />;
};

// /search → /yards (securspace-ui#3054): the in-app fallback for the CloudFront 301
// (securspace-aws#51), with the same mapping. A printable-ASCII city with a two-letter
// state becomes the path; anything else (state=Georgia, no city, an unreadable city)
// passes through as query params, which /yards still reads. Every other param rides along.
const LegacySearchRedirect = () => {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const city = params.get('city')?.trim() ?? '';
  const state = params.get('state')?.trim() ?? '';
  const { pathname } = /^[a-z]{2}$/i.test(state) && /^[ -~]+$/.test(city)
    ? yardsAddress({ city, state, country: params.get('country')?.trim() })
    : { pathname: '/yards' };
  if (pathname !== '/yards') ['city', 'state', 'country'].forEach((key) => params.delete(key));
  const rest = params.toString();
  return <Navigate replace to={{ pathname, search: rest ? `?${rest}` : '', hash }} />;
};

export const legacyRedirectRoutes = () => [
  ...Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
    <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
  )),
  <Route key="/search" path="/search" element={<LegacySearchRedirect />} />,
];
